import React, { useState, useEffect } from 'react';
import { Container, Button, Table, Modal, Form, Spinner, Tabs, Tab, Card, Row, Col } from 'react-bootstrap';
import apiClient, { API_ENDPOINTS } from '../utils/api';
import RecordCard from '../components/RecordCard';

const initialEmailForm = {
  from_email: '',
  to_email: '',
  subject: '',
  body: '',
  email_type: 'inbound'
};

const initialTemplateForm = {
  template_name: '',
  template_type: 'general',
  subject: '',
  body: ''
};

function Emails() {
  const [emails, setEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [autoRules, setAutoRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [emailForm, setEmailForm] = useState(initialEmailForm);
  const [templateForm, setTemplateForm] = useState(initialTemplateForm);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [emailsRes, templatesRes, rulesRes] = await Promise.all([
        apiClient.get(API_ENDPOINTS.EMAILS),
        apiClient.get(API_ENDPOINTS.EMAIL_TEMPLATES),
        apiClient.get(API_ENDPOINTS.AUTO_RULES)
      ]);

      setEmails(emailsRes.data);
      setTemplates(templatesRes.data);
      setAutoRules(rulesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTemplateChange = (e) => {
    const { name, value } = e.target;
    setTemplateForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateEmail = async () => {
    setSaving(true);
    try {
      await apiClient.post(API_ENDPOINTS.EMAILS, emailForm);
      setEmailForm(initialEmailForm);
      setShowEmailModal(false);
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTemplate = async () => {
    setSaving(true);
    try {
      await apiClient.post(API_ENDPOINTS.EMAIL_TEMPLATES, {
        ...templateForm,
        variables: {}
      });
      setTemplateForm(initialTemplateForm);
      setShowTemplateModal(false);
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  // 行アクション(テーブル/カードで共用)
  const renderEmailActions = () => (
    <>
      <Button variant="outline-primary" size="sm" className="me-2">詳細</Button>
      <Button variant="outline-success" size="sm">返信</Button>
    </>
  );

  const renderRuleActions = () => (
    <>
      <Button variant="outline-primary" size="sm" className="me-2">編集</Button>
      <Button variant="outline-danger" size="sm">削除</Button>
    </>
  );

  return (
    <Container fluid>
      <div className="page-header">
        <h1 className="page-title">メール管理</h1>
      </div>

      <Tabs defaultActiveKey="emails" className="mb-3">
        <Tab eventKey="emails" title="受信メール">
          <div className="mb-3">
            <Button variant="primary" onClick={() => setShowEmailModal(true)} className="me-2">
              + 手動追加
            </Button>
            <Button variant="outline-secondary">自動返信テスト</Button>
          </div>

          <div className="d-none d-lg-block">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>送信者</th>
                <th>件名</th>
                <th>ステータス</th>
                <th>自動返信</th>
                <th>受信日</th>
                <th>アクション</th>
              </tr>
            </thead>
            <tbody>
              {emails.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted">メールはまだ登録されていません</td>
                </tr>
              ) : (
                emails.map(email => (
                  <tr key={email.id}>
                    <td>{email.from_email}</td>
                    <td>{email.subject}</td>
                    <td>
                      <span className={`badge ${email.status === 'pending' ? 'bg-warning' : 'bg-success'}`}>
                        {email.status === 'pending' ? '未処理' : '処理済み'}
                      </span>
                    </td>
                    <td>
                      {email.auto_reply_sent ? (
                        <span className="badge bg-success">送信済み</span>
                      ) : (
                        <span className="badge bg-secondary">未送信</span>
                      )}
                    </td>
                    <td>{new Date(email.created_at).toLocaleDateString('ja-JP')}</td>
                    <td>{renderEmailActions()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          </div>

          {/* モバイル: カード型リスト */}
          <div className="d-lg-none">
            {emails.length === 0 ? (
              <p className="text-center text-muted">メールはまだ登録されていません</p>
            ) : (
              emails.map(email => (
                <RecordCard
                  key={email.id}
                  title={email.subject}
                  subtitle={email.from_email}
                  badges={[
                    <span key="s" className={`badge ${email.status === 'pending' ? 'bg-warning' : 'bg-success'}`}>
                      {email.status === 'pending' ? '未処理' : '処理済み'}
                    </span>,
                    email.auto_reply_sent ? (
                      <span key="a" className="badge bg-success">返信済み</span>
                    ) : (
                      <span key="a" className="badge bg-secondary">未返信</span>
                    ),
                  ]}
                  fields={[
                    { label: '受信日', value: new Date(email.created_at).toLocaleDateString('ja-JP') },
                  ]}
                  actions={renderEmailActions()}
                />
              ))
            )}
          </div>
        </Tab>

        <Tab eventKey="templates" title="テンプレート">
          <div className="mb-3">
            <Button variant="primary" onClick={() => setShowTemplateModal(true)}>
              + 新規テンプレート
            </Button>
          </div>

          <Row>
            {templates.length === 0 ? (
              <Col>
                <p className="text-muted">テンプレートはまだ登録されていません</p>
              </Col>
            ) : (
              templates.map(template => (
                <Col md={6} key={template.id} className="mb-3">
                  <Card>
                    <Card.Body>
                      <Card.Title>{template.template_name}</Card.Title>
                      <Card.Text>
                        <small className="text-muted">件名: {template.subject}</small>
                      </Card.Text>
                      <div>
                        <Button variant="outline-primary" size="sm" className="me-2">編集</Button>
                        <Button variant="outline-danger" size="sm">削除</Button>
                        {template.is_active && (
                          <span className="badge bg-success float-end">有効</span>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </Tab>

        <Tab eventKey="rules" title="自動返信ルール">
          <div className="mb-3">
            <Button variant="primary">+ 新規ルール</Button>
          </div>

          <div className="d-none d-lg-block">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>ルール名</th>
                <th>トリガータイプ</th>
                <th>テンプレート</th>
                <th>ステータス</th>
                <th>アクション</th>
              </tr>
            </thead>
            <tbody>
              {autoRules.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">自動返信ルールはまだ登録されていません</td>
                </tr>
              ) : (
                autoRules.map(rule => (
                  <tr key={rule.id}>
                    <td>{rule.rule_name}</td>
                    <td>{rule.trigger_type === 'keyword' ? 'キーワード' : 'ステータス'}</td>
                    <td>ID: {rule.template_id}</td>
                    <td>
                      <span className={`badge ${rule.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {rule.is_active ? '有効' : '無効'}
                      </span>
                    </td>
                    <td>{renderRuleActions()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          </div>

          {/* モバイル: カード型リスト */}
          <div className="d-lg-none">
            {autoRules.length === 0 ? (
              <p className="text-center text-muted">自動返信ルールはまだ登録されていません</p>
            ) : (
              autoRules.map(rule => (
                <RecordCard
                  key={rule.id}
                  title={rule.rule_name}
                  badges={[
                    <span key="s" className={`badge ${rule.is_active ? 'bg-success' : 'bg-secondary'}`}>
                      {rule.is_active ? '有効' : '無効'}
                    </span>,
                  ]}
                  fields={[
                    { label: 'トリガー', value: rule.trigger_type === 'keyword' ? 'キーワード' : 'ステータス' },
                    { label: 'テンプレート', value: `ID: ${rule.template_id}` },
                  ]}
                  actions={renderRuleActions()}
                />
              ))
            )}
          </div>
        </Tab>
      </Tabs>

      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>メール追加</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>送信元メール</Form.Label>
              <Form.Control
                type="email"
                name="from_email"
                value={emailForm.from_email}
                onChange={handleEmailChange}
                placeholder="guest@example.com"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>宛先メール</Form.Label>
              <Form.Control
                type="email"
                name="to_email"
                value={emailForm.to_email}
                onChange={handleEmailChange}
                placeholder="owner@example.com"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>件名</Form.Label>
              <Form.Control
                type="text"
                name="subject"
                value={emailForm.subject}
                onChange={handleEmailChange}
                placeholder="メール件名"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>本文</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="body"
                value={emailForm.body}
                onChange={handleEmailChange}
                placeholder="メール本文"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmailModal(false)}>キャンセル</Button>
          <Button
            variant="primary"
            onClick={handleCreateEmail}
            disabled={saving || !emailForm.from_email || !emailForm.to_email || !emailForm.subject || !emailForm.body}
          >
            {saving ? '追加中...' : '追加'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>メールテンプレート</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>テンプレート名</Form.Label>
              <Form.Control
                type="text"
                name="template_name"
                value={templateForm.template_name}
                onChange={handleTemplateChange}
                placeholder="テンプレート名"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>テンプレートタイプ</Form.Label>
              <Form.Select name="template_type" value={templateForm.template_type} onChange={handleTemplateChange}>
                <option value="check_in">チェックイン案内</option>
                <option value="check_out">チェックアウト確認</option>
                <option value="cancellation">キャンセル確認</option>
                <option value="general">一般問い合わせ</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>件名</Form.Label>
              <Form.Control
                type="text"
                name="subject"
                value={templateForm.subject}
                onChange={handleTemplateChange}
                placeholder="メール件名"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>本文</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                name="body"
                value={templateForm.body}
                onChange={handleTemplateChange}
                placeholder="メール本文"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>利用可能な変数</Form.Label>
              <small className="text-muted d-block">
                {'{guest_name}, {check_in_date}, {check_out_date}, {property_name}, {booking_id}'}
              </small>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>キャンセル</Button>
          <Button
            variant="primary"
            onClick={handleCreateTemplate}
            disabled={saving || !templateForm.template_name || !templateForm.subject || !templateForm.body}
          >
            {saving ? '作成中...' : '作成'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Emails;
